import { useMemo } from 'react';
import { useUser } from './useUser';

/**
 * Types de compte possibles
 */
export type AccountType = 'company' | 'financial_institution' | 'none';

/**
 * Hook pour déterminer le type de compte de l'utilisateur
 * 
 * Règles:
 * - Si companyId existe → compte entreprise (company)
 * - Si financialInstitutionId existe → compte institution financière
 * - Si aucun → nouveau compte (none), tout montrer
 */
export function useAccountType() {
  const { user, isLoading } = useUser();
  
  const accountInfo = useMemo(() => {
    // Si l'utilisateur n'est pas encore chargé, retourner un état de chargement
    if (isLoading || !user) {
      return {
        accountType: 'none' as AccountType,
        isLoading: true,
        isCompany: false,
        isFinancialInstitution: false,
        isNewAccount: true,
        companyId: undefined as string | undefined,
        financialInstitutionId: undefined as string | undefined,
      };
    }
    
    const hasCompany = !!(user.companyId && user.companyId.trim() !== '');
    const hasFinancialInstitution = !!(user.financialInstitutionId && user.financialInstitutionId.trim() !== '');
    
    // Déterminer le type de compte
    let accountType: AccountType = 'none';
    if (hasCompany) {
      accountType = 'company';
    } else if (hasFinancialInstitution) {
      accountType = 'financial_institution';
    }
    
    return {
      accountType,
      isLoading: false,
      isCompany: hasCompany,
      isFinancialInstitution: hasFinancialInstitution,
      isNewAccount: !hasCompany && !hasFinancialInstitution,
      companyId: user.companyId,
      financialInstitutionId: user.financialInstitutionId,
    };
  }, [user, isLoading]);
  
  return accountInfo;
}

/**
 * Hook pour vérifier si l'utilisateur peut accéder à une ressource spécifique
 */
export function useCanAccess(resourceType: 'company' | 'financial_institution' | 'both') {
  const { accountType, isLoading, isNewAccount } = useAccountType();
  
  const canAccess = useMemo(() => {
    // Pendant le chargement, ne pas bloquer
    if (isLoading) return true;
    
    // Un nouveau compte peut tout voir
    if (isNewAccount) return true;
    
    // Vérifier selon le type de ressource demandé
    switch (resourceType) {
      case 'company':
        return accountType === 'company';
      case 'financial_institution':
        return accountType === 'financial_institution';
      case 'both':
        return true;
      default:
        return false;
    }
  }, [accountType, isLoading, isNewAccount, resourceType]);
  
  return { canAccess, isLoading, accountType, isNewAccount };
}
